import { useState, useEffect, useRef } from "react";
import { Newspaper, Plus, X, Trash, Image, UploadSimple, ArrowSquareOut, CalendarBlank } from "@phosphor-icons/react";
import { useAdminAuth } from "@/contexts/AdminAuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminNews() {
  const { admin } = useAdminAuth();
  const canDelete = admin?.allow_delete !== false;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [form, setForm] = useState({ title: "", publication_name: "", news_link: "", file: null });
  const [logoPreview, setLogoPreview] = useState(null);
  const [error, setError] = useState("");
  const fileRef = useRef();

  const inputCls = "w-full px-3.5 py-2.5 rounded-lg border text-sm outline-none transition-colors";
  const inputStyle = { borderColor: "rgba(26,92,55,0.2)", backgroundColor: "#F7F5F0", color: "#1C211F" };
  const labelCls = "block text-xs font-medium mb-1.5 uppercase tracking-wider";

  const fetchItems = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/news`);
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setForm((f) => ({ ...f, file }));
    setLogoPreview(URL.createObjectURL(file));
  };

  const resetForm = () => {
    setForm({ title: "", publication_name: "", news_link: "", file: null });
    setLogoPreview(null);
    setError("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.file) { setError("Please upload a publication logo."); return; }
    setError("");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("publication_name", form.publication_name);
      fd.append("news_link", form.news_link);
      fd.append("file", form.file);
      const res = await fetch(`${BACKEND_URL}/api/news`, { method: "POST", body: fd });
      if (!res.ok) throw new Error("Upload failed");
      await fetchItems();
      resetForm();
      setDrawerOpen(false);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await fetch(`${BACKEND_URL}/api/news/${id}`, { method: "DELETE" });
      setItems((prev) => prev.filter((n) => n.id !== id));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="relative">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h3 className="font-heading text-xl font-medium" style={{ color: "#1C211F" }}>News</h3>
          <p className="text-sm mt-0.5" style={{ color: "#4A5450" }}>
            {items.length} news item{items.length !== 1 ? "s" : ""}
          </p>
        </div>
        <button
          onClick={() => { resetForm(); setDrawerOpen(true); }}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
          style={{ backgroundColor: "#1A5C37", color: "#F7F5F0" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#14482B")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#1A5C37")}
          data-testid="add-news-btn"
        >
          <Plus size={16} weight="bold" />
          Add News
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: "#1A5C37", borderTopColor: "transparent" }} />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: "rgba(26,92,55,0.08)" }}>
            <Newspaper size={28} weight="duotone" style={{ color: "#1A5C37" }} />
          </div>
          <p className="text-sm font-medium" style={{ color: "#1C211F" }}>No news items yet</p>
          <p className="text-xs mt-1" style={{ color: "#4A5450" }}>Click &quot;Add News&quot; to add your first one.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => (
            <div
              key={item.id}
              className="flex flex-col p-5 rounded-xl border"
              style={{ backgroundColor: "#fff", borderColor: "rgba(26,92,55,0.1)" }}
              data-testid={`news-item-${item.id}`}
            >
              {/* Top: logo + actions */}
              <div className="flex items-start justify-between gap-3 mb-3">
                <div
                  className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center"
                  style={{ backgroundColor: "rgba(26,92,55,0.06)", border: "1px solid rgba(26,92,55,0.1)" }}
                >
                  <img
                    src={`${BACKEND_URL}${item.publication_logo_url}`}
                    alt={item.publication_name}
                    className="w-full h-full object-contain"
                    onError={(e) => { e.target.style.display = "none"; e.target.parentNode.querySelector(".fallback-icon").style.display = "flex"; }}
                  />
                  <div className="fallback-icon w-full h-full hidden items-center justify-center">
                    <Image size={18} weight="duotone" style={{ color: "#1A5C37" }} />
                  </div>
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                  <a
                    href={item.news_link}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg border transition-colors"
                    style={{ color: "#1A5C37", borderColor: "rgba(26,92,55,0.2)" }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(26,92,55,0.06)")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                    data-testid={`news-link-${item.id}`}
                  >
                    <ArrowSquareOut size={13} weight="bold" />
                    Open
                  </a>
                  {canDelete && (
                    <button
                      onClick={() => handleDelete(item.id)}
                      disabled={deletingId === item.id}
                      className="p-1.5 rounded-lg transition-colors"
                      style={{ color: "#B85C38" }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(184,92,56,0.08)")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      data-testid={`delete-news-${item.id}`}
                    >
                      <Trash size={15} weight="duotone" />
                    </button>
                  )}
                </div>
              </div>

              {/* Publication name */}
              <p className="text-xs font-medium mb-1" style={{ color: "#1A5C37" }}>{item.publication_name}</p>

              {/* Title */}
              <p className="text-sm font-medium leading-snug line-clamp-3 mb-2" style={{ color: "#1C211F" }}>
                {item.title}
              </p>

              {/* Date */}
              {item.created_at && (
                <p className="flex items-center gap-1 text-xs mt-auto pt-2" style={{ color: "#7A8A84", borderTop: "1px solid rgba(26,92,55,0.06)" }}>
                  <CalendarBlank size={11} weight="duotone" />
                  {new Date(item.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Drawer Overlay */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-40"
          style={{ backgroundColor: "rgba(0,0,0,0.35)" }}
          onClick={() => setDrawerOpen(false)}
        />
      )}

      {/* Drawer Panel */}
      <div
        className={`fixed top-0 right-0 h-full z-50 flex flex-col transition-transform duration-300 ${drawerOpen ? "translate-x-0" : "translate-x-full"}`}
        style={{ width: "min(480px, 100vw)", backgroundColor: "#fff", boxShadow: "-8px 0 32px rgba(0,0,0,0.12)" }}
      >
        {/* Drawer Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: "rgba(26,92,55,0.1)" }}>
          <h3 className="font-heading text-lg font-medium" style={{ color: "#1C211F" }}>Add News</h3>
          <button
            onClick={() => setDrawerOpen(false)}
            className="p-1.5 rounded-lg transition-colors"
            style={{ color: "#4A5450" }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(26,92,55,0.08)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        {/* Drawer Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-6 space-y-5">

          {/* Publication Logo Upload */}
          <div>
            <label className={labelCls} style={{ color: "#4A5450" }}>Publication Logo</label>
            <div
              className="relative flex flex-col items-center justify-center gap-2 p-6 rounded-xl border-2 border-dashed cursor-pointer transition-colors"
              style={{ borderColor: logoPreview ? "#1A5C37" : "rgba(26,92,55,0.25)", backgroundColor: "#F7F5F0" }}
              onClick={() => fileRef.current?.click()}
            >
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
              {logoPreview ? (
                <img src={logoPreview} alt="logo preview" className="max-h-20 max-w-full object-contain rounded-lg" />
              ) : (
                <>
                  <UploadSimple size={24} weight="duotone" style={{ color: "#1A5C37" }} />
                  <p className="text-sm" style={{ color: "#4A5450" }}>Click to upload logo image</p>
                </>
              )}
              {logoPreview && (
                <button
                  type="button"
                  className="absolute top-2 right-2 p-1 rounded-full"
                  style={{ backgroundColor: "rgba(184,92,56,0.1)", color: "#B85C38" }}
                  onClick={(e) => { e.stopPropagation(); setLogoPreview(null); setForm((f) => ({ ...f, file: null })); if (fileRef.current) fileRef.current.value = ""; }}
                >
                  <X size={13} weight="bold" />
                </button>
              )}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className={labelCls} style={{ color: "#4A5450" }}>Title</label>
            <input
              required
              type="text"
              placeholder="News headline"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              className={inputCls}
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = "#1A5C37")}
              onBlur={(e) => (e.target.style.borderColor = "rgba(26,92,55,0.2)")}
              data-testid="news-title-input"
            />
          </div>

          {/* Publication Name */}
          <div>
            <label className={labelCls} style={{ color: "#4A5450" }}>Publication Name</label>
            <input
              required
              type="text"
              placeholder="e.g. The Hindu, Deccan Herald"
              value={form.publication_name}
              onChange={(e) => setForm((f) => ({ ...f, publication_name: e.target.value }))}
              className={inputCls}
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = "#1A5C37")}
              onBlur={(e) => (e.target.style.borderColor = "rgba(26,92,55,0.2)")}
              data-testid="news-publication-input"
            />
          </div>

          {/* News Link */}
          <div>
            <label className={labelCls} style={{ color: "#4A5450" }}>News Link</label>
            <input
              required
              type="url"
              placeholder="https://..."
              value={form.news_link}
              onChange={(e) => setForm((f) => ({ ...f, news_link: e.target.value }))}
              className={inputCls}
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = "#1A5C37")}
              onBlur={(e) => (e.target.style.borderColor = "rgba(26,92,55,0.2)")}
              data-testid="news-link-input"
            />
          </div>

          {error && (
            <p className="text-xs px-3 py-2 rounded-lg" style={{ color: "#B85C38", backgroundColor: "rgba(184,92,56,0.08)" }}>
              {error}
            </p>
          )}
        </form>

        {/* Drawer Footer */}
        <div className="px-6 py-4 border-t flex gap-3 flex-shrink-0" style={{ borderColor: "rgba(26,92,55,0.1)" }}>
          <button
            type="button"
            onClick={() => setDrawerOpen(false)}
            className="flex-1 py-2.5 rounded-lg text-sm font-medium border transition-colors"
            style={{ borderColor: "rgba(26,92,55,0.2)", color: "#4A5450" }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: submitting ? "#4a8c62" : "#1A5C37", color: "#F7F5F0", cursor: submitting ? "not-allowed" : "pointer" }}
            data-testid="submit-news-btn"
          >
            {submitting ? "Saving..." : "Save News"}
          </button>
        </div>
      </div>
    </div>
  );
}
